import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Snackbar, Alert, Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

/**
 * PWA Install Prompt Component
 * Shows a banner encouraging Solo users to install the app
 * 
 * Features:
 * - Only shows on Solo dashboard routes
 * - Android/Chrome: Shows "Install Now" button
 * - iOS/Safari: Shows manual installation instructions
 * - Respects user's dismiss preference (3 days)
 * - Detailed console logging for debugging
 */
const PWAInstallPrompt = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // Only show on Solo routes
  const isSoloRoute = location.pathname.includes('/solo-dashboard') || 
                      location.pathname.includes('/solo');

  useEffect(() => {
    // Don't show prompt if not on Solo route
    if (!isSoloRoute) {
      console.log('[PWA] Not on Solo route, skipping install prompt');
      return;
    }

    console.log('[PWA] Initializing install prompt on Solo route');

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    console.log('[PWA] iOS detected:', iOS);

    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true;
    
    if (standalone) {
      console.log('[PWA] App already installed, not showing prompt');
      setIsInstalled(true);
      return;
    }

    // Check if user previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed-solo');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const threeDays = 3 * 24 * 60 * 60 * 1000;
    
    if (dismissed && Date.now() - dismissedTime < threeDays) {
      const daysLeft = Math.ceil((threeDays - (Date.now() - dismissedTime)) / (1000 * 60 * 60 * 24));
      console.log(`[PWA] User dismissed prompt, will show again in ${daysLeft} days`);
      return;
    }

    // For non-iOS devices, listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] ✅ beforeinstallprompt event fired!');
      
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      console.log('[PWA] Deferred prompt saved, will show in 30 seconds');
      
      // Show install prompt after 30 seconds of use
      setTimeout(() => {
        console.log('[PWA] 30 seconds elapsed, showing install prompt');
        setShowInstallPrompt(true);
      }, 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show instructions after 30 seconds
    if (iOS) {
      console.log('[PWA] iOS detected, will show install instructions in 30 seconds');
      setTimeout(() => {
        console.log('[PWA] Showing iOS install instructions');
        setShowInstallPrompt(true);
      }, 30000);
    }

    // Detect if app was installed
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] ✅ App installed successfully!');
      setIsInstalled(true);
      setShowInstallPrompt(false);
      localStorage.removeItem('pwa-install-dismissed-solo');
    });

    // Debug: Log current state
    console.log('[PWA] Current state:', {
      isSoloRoute,
      isIOS: iOS,
      isInstalled: standalone,
      userAgent: navigator.userAgent
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isSoloRoute]);

  const handleInstallClick = async () => {
    if (isIOS) {
      // iOS doesn't support programmatic install
      console.log('[PWA] iOS detected, showing manual instructions');
      return;
    }

    if (!deferredPrompt) {
      console.log('[PWA] ⚠️ No deferred prompt available');
      return;
    }

    console.log('[PWA] Showing install prompt to user');

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log('[PWA] User choice:', outcome);

    if (outcome === 'accepted') {
      console.log('[PWA] ✅ User accepted the install prompt');
      setShowInstallPrompt(false);
    } else {
      console.log('[PWA] ❌ User dismissed the install prompt');
      handleDismiss();
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    console.log('[PWA] User dismissed install prompt');
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-install-dismissed-solo', Date.now().toString());
  };

  // Don't show if not on Solo route
  if (!isSoloRoute) {
    return null;
  }

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  // Don't show if prompt not ready
  if (!showInstallPrompt) {
    return null;
  }

  // iOS-specific instructions
  if (isIOS) {
    return (
      <Snackbar
        open={showInstallPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, sm: 24 } }}
      >
        <Alert
          severity="info"
          icon={<PhoneIphoneIcon />}
          sx={{
            width: '100%',
            maxWidth: 600,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Install SalonLogic Solo
            </Typography>
            <Typography variant="body2">
              Tap the <strong>Share</strong> button{' '}
              <Box component="span" sx={{ fontSize: 18 }}>⎙</Box>
              {' '}then select <strong>"Add to Home Screen"</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Works offline • Quick access from home screen
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    );
  }

  // Android/Desktop Chrome install button
  return (
    <Snackbar
      open={showInstallPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 80, sm: 24 } }}
    >
      <Alert
        severity="success"
        icon={<GetAppIcon />}
        sx={{
          width: '100%',
          maxWidth: 600,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleDismiss}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <strong>Install Solo App</strong>
            <br />
            <span style={{ fontSize: '0.875rem' }}>
              Quick access from your home screen - works offline!
            </span>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleInstallClick}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: 'white',
              color: 'success.main',
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
          >
            Install Now
          </Button>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default PWAInstallPrompt;